import { z } from "zod";

// TES-438/439 — medical source traceability.
//
// The patient sees a collapsed section "Källor (n)" after the content. An
// opened row shows title, publisher, year and the link "Öppna källa".
//
// The full claim→source mapping is INTERNAL and stays server-side. This file
// defines the narrow patient view and nothing else, so that the type system
// makes leaks hard rather than merely forbidden.

/**
 * Exactly the fields a patient may see about a source. Four, no more.
 *
 * This is a WHITELIST, and that is deliberate. A registry that grows new
 * internal fields — a review note, a QA flag, a reviewer comment — then
 * defaults to invisible. Had the projection instead stripped known sensitive
 * fields (a blacklist), the next field added would leak straight into the
 * patient surface, and nobody would have noticed until it was on screen.
 */
export const patientSourceSchema = z.object({
  title: z.string().min(1),
  issuingBody: z.string().min(1),
  year: z.number().int(),
  url: z.string().url(),
});
export type PatientSource = z.infer<typeof patientSourceSchema>;

/**
 * The source section for ONE patient surface, deduplicated.
 *
 * `count` is the number of unique sources, not the number of claims: three
 * claims in the same lesson all supported by the product information give
 * "Källor (1)".
 */
export const patientSourceSectionSchema = z.object({
  label: z.literal("Källor"),
  collapsedByDefault: z.literal(true),
  count: z.number().int().nonnegative(),
  sources: z.array(patientSourceSchema),
});
export type PatientSourceSection = z.infer<typeof patientSourceSectionSchema>;

/** Field names that must NEVER appear in a patient payload. */
export const INTERNAL_CITATION_FIELDS = [
  "claimId",
  "claim_id",
  "claimIds",
  "claim_ids",
  "claimTextSnapshot",
  "claim_text_snapshot",
  "medicalReviewStatus",
  "medical_review_status",
  "reviewNotes",
  "review_notes",
  "supportLevel",
  "support_level",
  "qualification",
  "contentRevisionId",
  "content_revision_id",
  "citationRequired",
  "citation_required",
  "paragraphId",
  "paragraph_id",
  "warnings",
  "pendingKirill",
  "pending_kirill",
  "copyRevisionRequired",
  "copy_revision_required",
] as const;

/**
 * Build the patient section from sources already fetched server-side.
 *
 * Deduplicates on url — the same source can support several claims in the
 * same view and must then be counted once. Sorts by year (newest first) and
 * then title, so the order is stable between calls; an unstable order makes
 * the section jump around between renders without the content having changed.
 *
 * Returns null when there are no sources: the contract says the section must
 * then not be shown at all, and an empty "Källor (0)" is not the same thing
 * as no section.
 */
export function buildPatientSourceSection(
  sources: PatientSource[],
): PatientSourceSection | null {
  const byUrl = new Map<string, PatientSource>();
  for (const s of sources) {
    if (!byUrl.has(s.url)) byUrl.set(s.url, s);
  }
  const unique = [...byUrl.values()].sort(
    (a, b) => b.year - a.year || a.title.localeCompare(b.title, "sv"),
  );
  if (unique.length === 0) return null;
  return {
    label: "Källor",
    collapsedByDefault: true,
    count: unique.length,
    sources: unique,
  };
}

// TES-441 — the body of `POST employee-api/v1/citations/import`.
//
// The registries are sent as they look in the package (snake_case), so what
// is imported is verbatim what sits in Drive. No rewriting on the way: every
// field we rename is a field that can start drifting from the source.
export const citationImportSourceSchema = z.object({
  source_id: z.string().min(1),
  title: z.string().min(1),
  issuing_body: z.string().min(1),
  year: z.number().int(),
  source_type: z.string().min(1),
  url: z.string().url(),
  supports: z.array(z.string()).optional(),
  status: z.string().optional(),
  doi: z.string().optional(),
  pmid: z.string().optional(),
  pmcid: z.string().optional(),
  isbn: z.string().optional(),
});

export const citationImportClaimSchema = z.object({
  claim_id: z.string().min(1),
  content_id: z.string().min(1),
  content_type: z.string().min(1),
  location: z.object({
    section: z.string(),
    lesson_id: z.string().nullable().optional(),
    paragraph_id: z.string().nullable().optional(),
  }),
  claim_text_snapshot: z.string().min(1),
  claim_kind: z.string().min(1),
  citation_required: z.boolean(),
  source_ids: z.array(z.string()),
  support_level: z.string().min(1),
  qualification: z.string().nullable().optional(),
  content_revision_id: z.string().min(1),
  medical_review_status: z.string().min(1),
  review_notes: z.string().nullable().optional(),
  patient_visible: z.boolean().optional(),
});

export const citationImportRequestSchema = z.object({
  registryVersion: z.string().min(1),
  retrievedAt: z.string().datetime(),
  sources: z.array(citationImportSourceSchema),
  claims: z.array(citationImportClaimSchema),
  // TES-468 — dry run: the whole import runs in a savepoint that is rolled
  // back, and the response carries the same warnings plus a surface diff. The
  // reviewer should see the diff before a new registry goes live.
  dryRun: z.boolean().default(false),
});
export type CitationImportRequest = z.infer<typeof citationImportRequestSchema>;
